import React, { useContext } from "react";
import PropTypes from "prop-types";
import { ConfigContext } from "../Config/ConfigContext";
import { useParams } from "react-router-dom";
import useApi from "../hooks/useApi";

/**
 * TenantInvite component handles accepting a tenant invite and switching tenants.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.redirectRoute - The route to redirect after switching tenants.
 * @returns {JSX.Element} The rendered component.
 */
const TenantInvite = ({ redirectRoute = "/" }) => {
	const { tenantId } = useParams();
	const { config } = useContext(ConfigContext);
	const [apiError, setApiError] = React.useState(null);
	const api = useApi();

	/**
	 * Accepts the tenant invite by calling the appropriate endpoint.
	 *
	 * @async
	 * @function acceptTenantInvite
	 * @returns {Promise<void>}
	 */
	async function acceptTenantInvite() {
		try {
			const result = await api.tenant.acceptInvite({ id: tenantId });
			if (result?.error) {
				setApiError(result.error + " " + result.errorType);
			}
		} catch (err) {
			setApiError(err.message);
		}
	}

	/**
	 * Switches tenants by calling the switching endpoint and then redirecting.
	 *
	 * @async
	 * @function switchTenants
	 * @returns {Promise<void>}
	 */
	async function switchTenants() {
		try {
			await api.tenant.switch({ id: tenantId });
			window.location.href = redirectRoute;
		} catch (err) {
			setApiError(err.message);
		}
	}

	return (
		<div className="tenantJoinedContainer">
			<div className="optionalBreakpointContainer">
				<h1 className="tenantJoinedHeader">Tenant successfully joined!</h1>
				<p className="tenantJoinedText">
					Ready to{" "}
					<button
						className="acceptInviteButton"
						onClick={async () => {
							await acceptTenantInvite();
							await switchTenants();
						}}
					>
						switch tenants?
					</button>
				</p>
				{apiError && (
					<div className="tenantApiError">
						<p>{apiError}</p>
					</div>
				)}
			</div>
		</div>
	);
};

TenantInvite.propTypes = {
	redirectRoute: PropTypes.string
};

export default TenantInvite;
